//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ILabelIndexObject } from '@apis/gatewayMediaApiModel';
import EditableName from '@components/atoms/editable-name/EditableName';
import { RequiredPermissionsActionButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { RelativeTimestamp } from '@components/atoms/timestamp/Timestamp';
import { TableDataCell } from '@components/organisms/table/TableDataCell';
import { TableRow } from '@components/organisms/table/TableRow';
import {
  ContentDrawerTrackingIds,
  IMAGE_TAGGING_PERMISSION,
} from '@content-drawer/contentDrawerConstants';
import { LabelDeleteRequest } from '@content-drawer/filters/labels/confirmations/LabelDeleteConfirmationDialog';
import { LabelUpdateRequest } from '@content-drawer/filters/labels/confirmations/LabelUpdateConfirmationDialog';
import { getHasUniversalImageActionsTaggingPermission } from '@content-drawer/selectors/getUniversalImageActionsPermissions';
import { Mixins } from '@theme/mixins';
import { commaSeparatedNumber } from '@utils/readableNumber';

export interface LabelRowProps {
  label: ILabelIndexObject;
  onDeleteLabel: (request: LabelDeleteRequest) => void;
  onUpdateLabel: (request: LabelUpdateRequest) => void;
}

export const LabelRow = ({
  label,
  onDeleteLabel,
  onUpdateLabel,
}: LabelRowProps) => {
  const hasEditPermission = useSelector(
    getHasUniversalImageActionsTaggingPermission,
  );

  return (
    <TableRow>
      <TableDataLabel>
        <EditableName
          disabledTooltip={
            "You don't have permission to update this label. Contact a group admin if this is a mistake."
          }
          isDisabled={!hasEditPermission}
          maxLength={50}
          name={label.userTag}
          onSaveName={async (name) =>
            onUpdateLabel({
              label: label.userTag,
              newLabel: name,
              count: label.itemsWithTag,
            })
          }
          placement='right'
          renameTooltip={`Click to update ${label.userTag}`}
          showEditIcon={hasEditPermission}
          textMixin={Mixins.Text.Body}
          trackingId={ContentDrawerTrackingIds.UPDATE_LABEL}
        />
      </TableDataLabel>
      <TableDataMediaItems>
        {commaSeparatedNumber(label.itemsWithTag)}
      </TableDataMediaItems>
      <TableDataLastUsed>
        <RelativeTimestamp timestamp={label.lastUsed} />
      </TableDataLastUsed>
      <DeleteColumn>
        <RequiredPermissionsActionButton
          interactive={false}
          onClick={() =>
            onDeleteLabel({
              label: label.userTag,
              count: label.itemsWithTag,
            })
          }
          permissionName={IMAGE_TAGGING_PERMISSION}
          tooltipContents={`You don't have permission to delete this label. Contact a group admin if this is a mistake.`}
          trackingId={ContentDrawerTrackingIds.DELETE_LABEL}
          iconName='delete'
          label='Delete Label'
        />
      </DeleteColumn>
    </TableRow>
  );
};

const TableDataLabel = styled(TableDataCell)(
  ({ theme }) => `
  max-width: calc(3 * ${theme.spacings[6]});
  padding: ${theme.spacings[3]} ${theme.spacings[0]} ${theme.spacings[3]} ${theme.spacings[3]};
`,
);

const TableDataMediaItems = styled(TableDataCell)(
  ({ theme }) => `
  padding: ${theme.spacings[3]};
  text-align: right;
`,
);

const TableDataLastUsed = styled(TableDataCell)(
  ({ theme }) => `
  padding: ${theme.spacings[3]};
`,
);

const DeleteColumn = styled(TableDataCell)(
  ({ theme }) => `
  width: ${theme.spacings[6]};
  padding: ${theme.spacings[3]};
  align-self: center;
  justify-self: center;
`,
);
